import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Lock, Star, Loader2 } from "lucide-react";

const TIER_ORDER = ["free", "poc", "growth", "pro"];
const TIER_LABELS = {
  free: "Free",
  poc: "POC",
  growth: "Growth",
  pro: "Pro",
};
const TIER_PRICES = {
  free: "Free forever",
  poc: "Founding rate locked in",
  growth: "Coming soon",
  pro: "Coming soon",
};

export default function VendorPlan() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/vendor/tier-features`);
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 text-[#F59E0B] animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { current_tier, tiers, feature_labels, founding_vendor } = data;
  const featureKeys = Object.keys(feature_labels);

  return (
    <div className="p-4 space-y-4" data-testid="vendor-plan">
      <h2 className="text-base font-bold text-white">Your Plan</h2>

      {/* Tier comparison table */}
      <div className="overflow-x-auto -mx-4 px-4">
        <div className="min-w-[600px]">
          {/* Tier headers */}
          <div className="grid grid-cols-5 gap-1 mb-2">
            <div /> {/* Feature label column */}
            {TIER_ORDER.map(tier => {
              const isCurrent = tier === current_tier;
              const isComingSoon = tier === "growth" || tier === "pro";
              return (
                <div
                  key={tier}
                  className={`text-center p-2 rounded-t-xl ${
                    isCurrent
                      ? "bg-[#F59E0B]/20 border-2 border-[#F59E0B] border-b-0"
                      : isComingSoon
                        ? "bg-white/5 opacity-50"
                        : "bg-[#2C2C2E]"
                  }`}
                  data-testid={`tier-header-${tier}`}
                >
                  <p className="text-xs font-black text-white">{TIER_LABELS[tier]}</p>
                  {tier === "poc" && founding_vendor && (
                    <span className="inline-flex items-center gap-0.5 text-[8px] bg-[#F59E0B] text-[#1C1C1E] px-1.5 py-0.5 rounded-full font-bold mt-1">
                      <Star className="w-2 h-2" /> Founding Vendor
                    </span>
                  )}
                  <p className="text-[9px] text-white/40 mt-1">{TIER_PRICES[tier]}</p>
                  {isCurrent && (
                    <span className="text-[8px] text-[#F59E0B] font-bold">CURRENT</span>
                  )}
                  {isComingSoon && (
                    <span className="text-[8px] text-white/30 font-bold">COMING SOON</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feature rows */}
          <div className="space-y-0.5">
            {featureKeys.map((fKey, i) => (
              <div
                key={fKey}
                className={`grid grid-cols-5 gap-1 ${i % 2 === 0 ? "bg-[#2C2C2E]/50" : ""}`}
                data-testid={`feature-row-${fKey}`}
              >
                <div className="p-2 flex items-center">
                  <span className="text-[10px] text-white/60 leading-tight">
                    {feature_labels[fKey]}
                  </span>
                </div>
                {TIER_ORDER.map(tier => {
                  const enabled = tiers[tier]?.[fKey];
                  const isComingSoon = tier === "growth" || tier === "pro";
                  const isCurrent = tier === current_tier;
                  return (
                    <div
                      key={tier}
                      className={`p-2 flex items-center justify-center ${
                        isCurrent
                          ? "bg-[#F59E0B]/5 border-x-2 border-[#F59E0B]"
                          : isComingSoon
                            ? "opacity-30"
                            : ""
                      }`}
                    >
                      {enabled ? (
                        <Check className="w-3.5 h-3.5 text-[#22C55E]" />
                      ) : (
                        <Lock className="w-3 h-3 text-white/20" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Bottom border for current tier */}
          <div className="grid grid-cols-5 gap-1">
            <div />
            {TIER_ORDER.map(tier => (
              <div
                key={tier}
                className={`h-1 rounded-b-xl ${
                  tier === current_tier ? "bg-[#F59E0B]" : "bg-transparent"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Founding vendor promise */}
      <div className="bg-[#2C2C2E] rounded-xl p-3 border border-white/5" data-testid="founding-promise">
        <p className="text-[11px] text-white/60 text-center leading-relaxed">
          Your founding vendor rate locks in for life as long as you stay on a paid plan.
        </p>
      </div>
    </div>
  );
}
